"use client";
import {bookingService} from "@/service/booking.service";
import {ManageBookingItem, ManageBookingResponse} from "@/types/booking";
import {Tab, Tabs} from "@nextui-org/react";
import {NextPage} from "next";
import {useEffect, useState} from "react";
import {Cancelled} from "./cancelled";
import {Completed} from "./completed";
import {getManageBookingFallbackRows} from "./manageBooking.shared";
import {NoShow} from "./noShow";
import {OnGoing} from "./onGoing";
import {UpComing} from "./upcoming";

export enum BookingStatus {
  ONGOING = "1",
  UPCOMING = "2",
  COMPLETED = "3",
  CANCELLED = "4",
  NOSHOW = "5"
}

export const ManageBookings: NextPage<{
  hasDateFilterChanged: boolean;
  searchValue: string;
  filterRequest: {
    from: number | null;
    to: number | null;
  };
  vehicleFilter?: string | null;
  driverFilter?: string;
}> = function ({
  hasDateFilterChanged,
  searchValue,
  filterRequest,
  vehicleFilter,
  driverFilter
}) {
  const [activeTab, setActiveTab] = useState<string>(BookingStatus.ONGOING);
  const [manageBookingResponse, setManageBookingResponse] = useState<
    ManageBookingItem[]
  >([]);
  let tabs = [
    {
      id: BookingStatus.ONGOING,
      label: "Ongoing"
    },
    {
      id: BookingStatus.UPCOMING,
      label: "Upcoming"
    },
    {
      id: BookingStatus.COMPLETED,
      label: "Completed"
    },
    {
      id: BookingStatus.CANCELLED,
      label: "Cancelled"
    },
    {
      id: BookingStatus.NOSHOW,
      label: "No Show"
    }
  ];
  const onSelectionChange = (tabid: any) => {
    setActiveTab(tabid);
  };

  const fetchDataManageBooking = async () => {
    const fallbackRows = getManageBookingFallbackRows(Number(activeTab));

    if (!hasDateFilterChanged || !filterRequest.from || !filterRequest.to) {
      setManageBookingResponse(fallbackRows);
      return;
    }

    const request = {
      ...filterRequest,
      filters: {
        status: [activeTab]
      }
    };

    try {
      const response: ManageBookingResponse =
        await bookingService.manageBooking(request);

      if (response?.reportData?.length) {
        setManageBookingResponse(response.reportData);
      } else {
        setManageBookingResponse(fallbackRows);
      }
    } catch {
      setManageBookingResponse(fallbackRows);
    }
  };

  useEffect(() => {
    fetchDataManageBooking();
  }, [activeTab, filterRequest, hasDateFilterChanged]);

  const filterData: ManageBookingItem[] | null =
    manageBookingResponse &&
    manageBookingResponse.filter(item => {
      const matchesSearch = item.name
        ?.toLowerCase()
        .includes(searchValue.toLowerCase());
      const matchesVehicle =
        !vehicleFilter ||
        item.vehicleName?.toLowerCase() === vehicleFilter.toLowerCase();
      const matchesDriver =
        !driverFilter ||
        item.driverName?.toLowerCase().includes(driverFilter.toLowerCase());

      return matchesSearch && matchesVehicle && matchesDriver;
    });

  return (
    <>
      {/* top display */}
      <div className="mb-2 flex gap-2">
        {/* 1 */}
        <div className="flex h-24 w-52 items-center rounded-2xl border border-gray-300  bg-[#FFFFFF] px-3 py-2">
          <div className="flex flex-row items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-100">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M23.8624 2.54337C24.1451 1.85684 23.9835 1.06647 23.4585 0.54148C22.9335 0.0164888 22.143 -0.145047 21.4564 0.137641L1.14758 8.4452C0.328302 8.77981 -0.133263 9.64518 0.0340542 10.5105C0.201372 11.3759 0.962954 11.999 1.8457 11.999H12.0001V22.1527C12.0001 23.0353 12.6232 23.7911 13.4887 23.9642C14.3541 24.1372 15.2195 23.6699 15.5542 22.8507L23.8624 2.54337Z"
                  fill="#2563EB"
                />
              </svg>
            </div>
            <div className=" flex flex-col">
              <div className="text text-sm text-gray-800">Ongoing</div>
              <div className=" text-2xl font-bold text-blue-500">14</div>
              <div className="text-xs  text-gray-400">
                Currently In Progress
              </div>
            </div>
          </div>
        </div>
        {/* 2 */}
        <div className="flex h-24 w-52 items-center rounded-2xl border border-gray-300 bg-[#FFFFFF] px-3 py-2">
          <div className="flex flex-row items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-orange-100">
              <svg
                width="20"
                height="21"
                viewBox="0 0 22 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M20.1667 1.84615H17.4167V0.923077C17.4167 0.678262 17.3201 0.443473 17.1482 0.270363C16.9763 0.0972524 16.7431 0 16.5 0C16.2569 0 16.0237 0.0972524 15.8518 0.270363C15.6799 0.443473 15.5833 0.678262 15.5833 0.923077V1.84615H6.41667V0.923077C6.41667 0.678262 6.32009 0.443473 6.14818 0.270363C5.97627 0.0972524 5.74312 0 5.5 0C5.25689 0 5.02373 0.0972524 4.85182 0.270363C4.67991 0.443473 4.58333 0.678262 4.58333 0.923077V1.84615H1.83333C1.3471 1.84615 0.880787 2.04066 0.536971 2.38688C0.193154 2.7331 0 3.20268 0 3.69231V22.1538C0 22.6435 0.193154 23.1131 0.536971 23.4593C0.880787 23.8055 1.3471 24 1.83333 24H20.1667C20.6529 24 21.1192 23.8055 21.463 23.4593C21.8068 23.1131 22 22.6435 22 22.1538V3.69231C22 3.20268 21.8068 2.7331 21.463 2.38688C21.1192 2.04066 20.6529 1.84615 20.1667 1.84615ZM5.95833 19.3846C5.68638 19.3846 5.42054 19.3034 5.19442 19.1513C4.96831 18.9991 4.79207 18.7829 4.688 18.5299C4.58393 18.2769 4.5567 17.9985 4.60975 17.7299C4.66281 17.4613 4.79376 17.2146 4.98606 17.0209C5.17836 16.8273 5.42336 16.6954 5.69008 16.642C5.95681 16.5886 6.23327 16.616 6.48452 16.7208C6.73577 16.8256 6.95052 17.003 7.1016 17.2307C7.25269 17.4584 7.33333 17.7261 7.33333 18C7.33333 18.3672 7.18847 18.7194 6.93061 18.9791C6.67274 19.2387 6.32301 19.3846 5.95833 19.3846ZM11 19.3846C10.7281 19.3846 10.4622 19.3034 10.2361 19.1513C10.01 18.9991 9.83374 18.7829 9.72967 18.5299C9.6256 18.2769 9.59837 17.9985 9.65142 17.7299C9.70448 17.4613 9.83543 17.2146 10.0277 17.0209C10.22 16.8273 10.465 16.6954 10.7318 16.642C10.9985 16.5886 11.2749 16.616 11.5262 16.7208C11.7774 16.8256 11.9922 17.003 12.1433 17.2307C12.2944 17.4584 12.375 17.7261 12.375 18C12.375 18.3672 12.2301 18.7194 11.9723 18.9791C11.7144 19.2387 11.3647 19.3846 11 19.3846ZM11 14.7692C10.7281 14.7692 10.4622 14.688 10.2361 14.5359C10.01 14.3837 9.83374 14.1675 9.72967 13.9145C9.6256 13.6615 9.59837 13.3831 9.65142 13.1145C9.70448 12.8459 9.83543 12.5992 10.0277 12.4055C10.22 12.2119 10.465 12.08 10.7318 12.0266C10.9985 11.9732 11.2749 12.0006 11.5262 12.1054C11.7774 12.2102 11.9922 12.3877 12.1433 12.6154C12.2944 12.8431 12.375 13.1108 12.375 13.3846C12.375 13.7518 12.2301 14.104 11.9723 14.3637C11.7144 14.6234 11.3647 14.7692 11 14.7692ZM16.0417 19.3846C15.7697 19.3846 15.5039 19.3034 15.2778 19.1513C15.0516 18.9991 14.8754 18.7829 14.7713 18.5299C14.6673 18.2769 14.64 17.9985 14.6931 17.7299C14.7461 17.4613 14.8771 17.2146 15.0694 17.0209C15.2617 16.8273 15.5067 16.6954 15.7734 16.642C16.0401 16.5886 16.3166 16.616 16.5679 16.7208C16.8191 16.8256 17.0338 17.003 17.1849 17.2307C17.336 17.4584 17.4167 17.7261 17.4167 18C17.4167 18.3672 17.2718 18.7194 17.0139 18.9791C16.7561 19.2387 16.4063 19.3846 16.0417 19.3846ZM16.0417 14.7692C15.7697 14.7692 15.5039 14.688 15.2778 14.5359C15.0516 14.3837 14.8754 14.1675 14.7713 13.9145C14.6673 13.6615 14.64 13.3831 14.6931 13.1145C14.7461 12.8459 14.8771 12.5992 15.0694 12.4055C15.2617 12.2119 15.5067 12.08 15.7734 12.0266C16.0401 11.9732 16.3166 12.0006 16.5679 12.1054C16.8191 12.2102 17.0338 12.3877 17.1849 12.6154C17.336 12.8431 17.4167 13.1108 17.4167 13.3846C17.4167 13.7518 17.2718 14.104 17.0139 14.3637C16.7561 14.6234 16.4063 14.7692 16.0417 14.7692ZM20.1667 7.38462H1.83333V3.69231H4.58333V4.61538C4.58333 4.8602 4.67991 5.09499 4.85182 5.2681C5.02373 5.44121 5.25689 5.53846 5.5 5.53846C5.74312 5.53846 5.97627 5.44121 6.14818 5.2681C6.32009 5.09499 6.41667 4.8602 6.41667 4.61538V3.69231H15.5833V4.61538C15.5833 4.8602 15.6799 5.09499 15.8518 5.2681C16.0237 5.44121 16.2569 5.53846 16.5 5.53846C16.7431 5.53846 16.9763 5.44121 17.1482 5.2681C17.3201 5.09499 17.4167 4.8602 17.4167 4.61538V3.69231H20.1667V7.38462Z"
                  fill="#F28A03"
                />
              </svg>
            </div>
            <div className=" flex flex-col">
              <div className="text text-sm text-gray-800">Upcoming</div>
              <div className=" text-2xl font-bold text-orange-400">100</div>
              <div className="text-xs text-gray-600">Scheduled Trips</div>
            </div>
          </div>
        </div>

        {/* 3 */}
        <div className="flex h-24 w-52 items-center rounded-2xl border border-gray-300 bg-[#FFFFFF] px-3 py-2">
          <div className="flex flex-row items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-green-100">
              <svg
                width="21"
                height="20"
                viewBox="0 0 26 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M13 25C16.4478 25 19.7544 23.683 22.1924 21.3388C24.6304 18.9946 26 15.8152 26 12.5C26 9.18479 24.6304 6.00537 22.1924 3.66117C19.7544 1.31696 16.4478 0 13 0C9.55219 0 6.24558 1.31696 3.80761 3.66117C1.36964 6.00537 0 9.18479 0 12.5C0 15.8152 1.36964 18.9946 3.80761 21.3388C6.24558 23.683 9.55219 25 13 25ZM18.7383 10.2051L12.2383 16.4551C11.7609 16.9141 10.9891 16.9141 10.5168 16.4551L7.2668 13.3301C6.78945 12.8711 6.78945 12.1289 7.2668 11.6748C7.74414 11.2207 8.51602 11.2158 8.98828 11.6748L11.375 13.9697L17.0117 8.54492C17.4891 8.08594 18.2609 8.08594 18.7332 8.54492C19.2055 9.00391 19.2105 9.74609 18.7332 10.2002L18.7383 10.2051Z"
                  fill="#349C42"
                />
              </svg>
            </div>
            <div className=" flex flex-col">
              <div className="text text-sm text-gray-800">Completed</div>
              <div className=" text-2xl font-bold text-green-500">14</div>
            </div>
          </div>
        </div>
        {/* 4 */}
        <div className="flex h-24 w-52 items-center rounded-2xl border border-gray-300 bg-[#FFFFFF] px-3 py-2">
          <div className="flex flex-row items-center gap-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-red-100">
              <svg
                width="21"
                height="20"
                viewBox="0 0 24 23"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 23C15.1826 23 18.2348 21.7884 20.4853 19.6317C22.7357 17.4751 24 14.55 24 11.5C24 8.45001 22.7357 5.52494 20.4853 3.36827C18.2348 1.2116 15.1826 0 12 0C8.8174 0 5.76516 1.2116 3.51472 3.36827C1.26428 5.52494 0 8.45001 0 11.5C0 14.55 1.26428 17.4751 3.51472 19.6317C5.76516 21.7884 8.8174 23 12 23ZM8.20312 7.86133C8.64375 7.43906 9.35625 7.43906 9.79219 7.86133L11.9953 9.97266L14.1984 7.86133C14.6391 7.43906 15.3516 7.43906 15.7875 7.86133C16.2234 8.28359 16.2281 8.96641 15.7875 9.38418L13.5844 11.4955L15.7875 13.6068C16.2281 14.0291 16.2281 14.7119 15.7875 15.1297C15.3469 15.5475 14.6344 15.552 14.1984 15.1297L11.9953 13.0184L9.79219 15.1297C9.35156 15.552 8.63906 15.552 8.20312 15.1297C7.76719 14.7074 7.7625 14.0246 8.20312 13.6068L10.4062 11.4955L8.20312 9.38418C7.7625 8.96191 7.7625 8.2791 8.20312 7.86133Z"
                  fill="#FC2A3D"
                />
              </svg>
            </div>
            <div className=" flex flex-col">
              <div className="text text-sm text-gray-800">Cancelled</div>
              <div className=" text-2xl font-bold text-red-500">14</div>
            </div>
          </div>
        </div>
        {/* 5 */}
        <div className="flex h-24 w-52 items-center rounded-2xl border border-gray-300 bg-[#FFFFFF] px-3 py-2">
          <div className="flex flex-row items-center gap-4">
            <div className="flex h-10 w-10 flex-col items-center justify-center gap-1 rounded-md bg-gray-200">
              <div>
                <svg
                  width="20"
                  height="13"
                  viewBox="0 0 24 13"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.0734 13C11.535 13.0011 11.0017 12.8953 10.5044 12.6889C10.0071 12.4825 9.55569 12.1795 9.17627 11.7975L2.40234 5.02821L5.0302 2.40035L11.7995 9.17428C11.8728 9.24384 11.97 9.28262 12.0711 9.28262C12.1722 9.28262 12.2694 9.24384 12.3427 9.17428L20.9738 0.543213L23.6016 3.17107L14.9706 11.7975C14.5911 12.1795 14.1397 12.4825 13.6424 12.6889C13.1451 12.8953 12.6118 13.0011 12.0734 13Z"
                    fill="#4B5563"
                  />
                  <path
                    d="M3.71429 11.1429H0V1.85714C0 1.3646 0.195663 0.892227 0.543945 0.543945C0.892227 0.195663 1.3646 0 1.85714 0H11.1429V3.71429H3.71429V11.1429Z"
                    fill="#4B5563"
                  />
                </svg>
              </div>
              <div>
                <svg
                  width="20"
                  height="4"
                  viewBox="0 0 26 4"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M26 0H0V3.71429H26V0Z" fill="#4B5563" />
                </svg>
              </div>
            </div>
            <div className=" flex flex-col">
              <div className="text text-sm">No Show</div>
              <div className=" text-2xl font-bold text-gray-500">14</div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex min-h-[calc(100vh-63px)] w-full flex-col p-1">
        <div className="flex w-full items-center justify-between gap-4">
          <div className="w-[500px]">
            <Tabs
              aria-label="Dynamic tabs"
              selectedKey={activeTab}
              variant="underlined"
              onSelectionChange={key => setActiveTab(key as string)}
              className="w-full"
              classNames={{
                tabList: "flex w-full",
                tab: "flex-1 text-center text-xs font-semibold ",
                cursor: "!bg-[#2563EB]",
                tabContent:
                  "!text-gray-700 group-data-[selected=true]:!text-[#2563EB]"
              }}
            >
              {tabs.map(item => (
                <Tab key={item.id} title={item.label}></Tab>
              ))}
            </Tabs>
          </div>
        </div>

        {activeTab === BookingStatus.ONGOING && (
          <div>
            <OnGoing filterData={filterData} />
          </div>
        )}
        {activeTab === BookingStatus.UPCOMING && (
          <div>
            <UpComing filterData={filterData} />
          </div>
        )}
        {activeTab === BookingStatus.COMPLETED && (
          <div>
            <Completed filterData={filterData} />
          </div>
        )}
        {activeTab === BookingStatus.CANCELLED && (
          <div>
            <Cancelled filterData={filterData} />
          </div>
        )}
        {activeTab === BookingStatus.NOSHOW && (
          <div>
            <NoShow filterData={filterData} />
          </div>
        )}
      </div>
    </>
  );
};
